import { useContext, useEffect, useState, useRef, createContext } from 'react';
import React from 'react';

import {
  IdToken,
  Session,
  instantiate,
  generateConfig,
} from '@agero/js-identity';
import { DateTime } from 'luxon';
import { useConfig } from './useConfig';
import { createTypedLogger } from '../utils/typedLogger';

import {
  STORAGE_KEY_USER_EXTENSION,
  STORAGE_CURRENT_CASE_ID,
  STORAGE_KEY_AGENT_EXTENSION_FROM_API,
  STORAGE_KEY_CALL911STATUS,
  STORAGE_PREVIOUS_CASE_ID,
} from '../constants';
import { useLocalStorage } from './useLocalStorage';

const logger = createTypedLogger('Auth');

export interface AuthContextStateProps {
  idToken: IdToken | null;
  lastActivityTime: DateTime;
  getSession: () => Promise<Session | undefined>;
  isUserSignedIn: () => boolean;
  parseRedirect: () => Promise<void>;
  signOut: () => Promise<void>;
  registerActivity: () => void;
  getUserEmail: () => string;
  getRawIdToken: () => string;
  getDecodedIdToken: () => any;
  getUserName: () => string;
  isBasicUser: () => boolean;
}

export interface AuthContextProps {
  auth: AuthContextStateProps;
}

export const AuthContext = createContext<AuthContextProps | null>(null);

export const AuthenticationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [authProps, setAuthProps] = useState<AuthContextProps | null>(null);
  const [needsRefresh, setNeedsRefresh] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const config = useConfig();
  const { removeLocalStorage, withLocalStorage } = useLocalStorage();

  const authConfig = generateConfig(
    {
      clientId: config.auth.clientId,
      redirectUri: config.auth.redirectUriSignIn,
      signOutUri: config.auth.redirectUriSignOut,
      scopes: config.auth.tokenScopesArray,
    },
    config.auth.idpEnvironment as any,
  );

  authConfig.errorCallback = async (err: any) => {
    logger.error('Authentication error:', err);
  };

  authConfig.refreshCallback = async () => {
    logger.info('Token refreshed successfully');
    setNeedsRefresh(true);
  };

  authConfig.signOutCallback = async () => {
    // Clean up local storage on sign out
    removeLocalStorage([
      STORAGE_KEY_USER_EXTENSION,
      STORAGE_CURRENT_CASE_ID,
      STORAGE_KEY_AGENT_EXTENSION_FROM_API,
      STORAGE_KEY_CALL911STATUS,
      STORAGE_PREVIOUS_CASE_ID,
    ]);
    logger.info('User signed out');
  };

  const auth = useRef(instantiate(authConfig));

  const getCurrentState = () => {
    const activityTimeString = auth.current.defaultSession.lastActivityTime;
    const isUserSignedIn = auth.current.isUserSignedIn();
    return {
      lastActivityTime: activityTimeString
        ? DateTime.fromISO(activityTimeString)
        : DateTime.utc(),
      isUserSignedIn,
      idToken: isUserSignedIn ? auth.current.defaultSession.idToken : null,
    };
  };

  const parseRedirect = async () => {
    await auth.current.parseRedirect();
    setNeedsRefresh(true);
  };

  const signOut = async () => {
    removeLocalStorage([
      STORAGE_KEY_USER_EXTENSION,
      STORAGE_CURRENT_CASE_ID,
      STORAGE_KEY_AGENT_EXTENSION_FROM_API,
      STORAGE_KEY_CALL911STATUS,
      STORAGE_PREVIOUS_CASE_ID,
    ]);
    await auth.current.signOut();
  };

  const registerActivity = () => {
    auth.current.defaultSession.registerActivity();
    setNeedsRefresh(true);
  };

  const getUserEmail = () => {
    return auth.current.defaultSession.idToken.email;
  };

  const getRawIdToken = () => {
    return auth.current.defaultSession.rawIdToken;
  };

  const getDecodedIdToken = () => {
    return JSON.parse(withLocalStorage('ageroAuth.default.decodedIdToken'));
  };

  const getSession = async () => {
    return auth.current.getSession();
  };

  // Authorization helpers
  const isAuthorizedForApp = (app: string): boolean => {
    try {
      const idToken = auth.current.defaultSession.idToken;
      if (!idToken) return false;

      const authorizedApps =
        idToken.getParsed<string[]>('authorizedApps') || [];

      return authorizedApps.includes(app);
    } catch (error) {
      return false;
    }
  };

  const isBasicUser = (): boolean => {
    const app = config.auth.basicUser;
    return isAuthorizedForApp(app);
  };

  const getUserName = () => {
    return auth.current.defaultSession.idToken.username;
  };

  const isUserSignedIn = (): boolean => {
    return getCurrentState().isUserSignedIn;
  };

  useEffect(() => {
    if (!needsRefresh) {
      return;
    }
    const currentState = getCurrentState();

    const authPropsData = {
      idToken: currentState.idToken,
      lastActivityTime: currentState.lastActivityTime,
      parseRedirect,
      signOut,
      registerActivity,
      getSession,
      getUserName,
      getUserEmail,
      isBasicUser,
      isUserSignedIn,
      getRawIdToken,
      getDecodedIdToken,
    };
    setAuthProps({ auth: authPropsData });
    setNeedsRefresh(false);
    setIsLoading(false);
  }, [auth, needsRefresh]);

  useEffect(() => {
    auth.current = instantiate(authConfig);
  }, [authConfig]);

  if (isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider value={authProps}>{children}</AuthContext.Provider>
  );
};

export function useAuthentication(): AuthContextProps {
  const auth = useContext(AuthContext);
  return auth ? auth : assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useAuthentication outside of AuthenticationProvider');
}
